import React, { useContext, useEffect, useRef } from "react";
import * as THREE from "three";

import { SharedMaterialsStateContext } from "../store/sharedMaterialsState/SharedMaterialsStateProvider";

import Material from "../model/material/Material";
import MaterialTypeEnum from "../enums/MaterialTypeEnum";
import ColorMaterial from "../model/material/ColorMaterial";
import { SharedMaterialsStateActionEnum } from "../store/sharedMaterialsState/interfaces";

import MaterialPair from "../model/material/MaterialPair";
import { useProductModelStore } from "../store/productModelStore";
import { useViewerStateStore } from "../store/viewerStateStore";
import { IMaterialSet } from "../services/MaterialManager";
import TextureMaterial from "../model/material/TextureMaterial";
import { sceneUnitsFromMm } from "../utils/scaler";

const loader = new THREE.TextureLoader();
const azurePath = "https://gb3dassets.z6.web.core.windows.net/assetbundles/Components/";

// Surface UVs are in scene units, so a texture would otherwise span 1x1 scene units regardless of its
// pixel dimensions. Textures carry no physical size, so span a fixed width and keep the image aspect ratio.
const TEXTURE_WIDTH = sceneUnitsFromMm(1000);

interface ITiledTextures {
    texture: THREE.Texture;
    // For floor and ceiling coverings: boards and tiles run along the image's long axis, and that must follow
    // the covering's local X axis (its texture direction), so portrait images are turned 90 degrees.
    horizontalTexture: THREE.Texture;
}

const loadTiledTextures = (url: string) : ITiledTextures => {
    let horizontalTexture : THREE.Texture | undefined;
    const texture = loader.load(url, t => {
        const image = t.image as { width: number, height: number };
        const height = TEXTURE_WIDTH * image.height / image.width;
        t.repeat.set(1 / TEXTURE_WIDTH, 1 / height);

        if (!horizontalTexture) return;
        if (image.height > image.width) {
            // Repeat is applied before rotation, so the surface's X axis is scaled by the image height
            horizontalTexture.repeat.set(1 / height, 1 / TEXTURE_WIDTH);
            horizontalTexture.rotation = Math.PI / 2;
        } else {
            horizontalTexture.repeat.copy(t.repeat);
        }
        // Shares the loaded image, but the renderer uploads per texture version
        horizontalTexture.needsUpdate = true;
    });
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    horizontalTexture = texture.clone();
    return { texture, horizontalTexture };
}

// Releases GPU resources held by a set of material pairs.
export const disposeMaterialPairs = (pairs: Record<number, MaterialPair>) => {
    Object.values(pairs).forEach(pair => {
        const material = pair.threeJsMaterial;
        if (!material) return;
        if (material instanceof THREE.MeshStandardMaterial) {
            material.map?.dispose();
            material.normalMap?.dispose();
        } else if (material instanceof THREE.MeshBasicMaterial) {
            material.map?.dispose();
        }
        material.dispose();

        const horizontalMaterial = pair.threeJsHorizontalMaterial as THREE.MeshStandardMaterial | undefined;
        horizontalMaterial?.map?.dispose();
        horizontalMaterial?.normalMap?.dispose();
        horizontalMaterial?.dispose();
    });
};

//https://gb3dassets.z6.web.core.windows.net/assetbundles/Components/CENT_Dyyni_Sisustuspaneeli/CENT_Dyyni_Sisustuspaneeli.png

const createMaterialPairs = (materials: Material[], isDebugMode: boolean  ) : Record<number, MaterialPair> => {
    const threeJsMaterialTypes = [MaterialTypeEnum.ColorMaterial,MaterialTypeEnum.TextureMaterial];
                                 
    const materialPairs : Record<number, MaterialPair> = {};
        materials.forEach( m => {
            if (m.materialType == MaterialTypeEnum.TextureMaterial) {
                const { material, horizontalMaterial } = createTextureMaterials(m as TextureMaterial);
                materialPairs[m.id] = new MaterialPair(m, material, horizontalMaterial);
                return;
            }
            materialPairs[m.id] = new MaterialPair(m, threeJsMaterialTypes.includes(m.materialType) 
                                                                ? createThreeJsMaterial(m) 
                                                                : undefined);
        });

    return materialPairs;
}

const createThreeJsMaterial = (input: Material) : THREE.Material => {

    

    switch (input.materialType)
    {
        case MaterialTypeEnum.ColorMaterial:
            const cm = input as ColorMaterial;
            return new THREE.MeshBasicMaterial({color: cm.color, transparent: cm.opacity < 1.0, opacity: cm.opacity });  // TODO: had to disable support for opacity since it was causing issues where text would not be visible, even on top of this material
            //return new THREE.MeshBasicMaterial({color: cm.color});        

        case MaterialTypeEnum.TextureMaterial:
            return createTextureMaterials(input as TextureMaterial).material;
    }

    throw "Invalid material";
    
}

const createTextureMaterials = (tm: TextureMaterial) : { material: THREE.Material, horizontalMaterial: THREE.Material } => {
    const mainTextures = loadTiledTextures(azurePath + tm.mainTextureUrl);
    const normalTextures = tm.normalMapUrl ? loadTiledTextures(azurePath + tm.normalMapUrl) : undefined;

    const create = (map: THREE.Texture, normalMap?: THREE.Texture) => new THREE.MeshStandardMaterial({
        map,                        // base color / albedo
        // You can add more maps later: roughnessMap, etc.
        ...(normalMap ? { normalMap } : {}),
        metalness: 0.1,
        roughness: 0.7,
    });

    return {
        material: create(mainTextures.texture, normalTextures?.texture),
        horizontalMaterial: create(mainTextures.horizontalTexture, normalTextures?.horizontalTexture),
    };
}

const SharedMaterialsCreator = () =>
{
    const { materials } = useProductModelStore();
    const isDebugMode = useViewerStateStore(state => state.isDebugMode);
    const { dispatch: materialsDispatch } = useContext(SharedMaterialsStateContext);
    const previousMaterialPairsRef = useRef<Record<number, MaterialPair> | null>(null);

    // When materials change, create three.js materials from them
     useEffect( () => {

        const materialPairs = createMaterialPairs(materials, isDebugMode);
        previousMaterialPairsRef.current = materialPairs;
        materialsDispatch({type: SharedMaterialsStateActionEnum.SET_MATERIALS, data: materialPairs });

        return () => {
            // Dispose GPU resources when the effect re-runs (new materials) or component unmounts.
            if (previousMaterialPairsRef.current) {
                disposeMaterialPairs(previousMaterialPairsRef.current);
                previousMaterialPairsRef.current = null;
            }
        };

      }, [materials, isDebugMode]);
    

    return <></>
};

export default SharedMaterialsCreator;
export { createMaterialPairs };